/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from "react";
import Offcanvas from "react-bootstrap/Offcanvas";
import Button from "react-bootstrap/Button";
import { useAppContext } from "../../AppContext";
import arrowLeft from "../../assets/arrow-left.svg";
import MobilePageShell from "../../components/commons/MobilePageShell";
import { getParsedLocalStorageItem } from "../../components/Navbar/Navbar";
import { trackEvent } from "../../mixpanel";
import {
  transformPath,
  localStorageGetItem,
  localStorageSetItem,
} from "../../App";
import { useNavigate } from "react-router-dom";
import productRoute from "../../services/productRoute";
import SubscriptionModal from "../../components/Modal/SubscriptionModal";
import CartItem from "../../components/commons/CartItem";
import SuggestedProducts from "../../components/commons/SuggestedProducts";
import CartIncentive from "../../components/Cart/CartIncentive";
import CartEmptyState from "../../components/Cart/CartEmptyState";
import { useGlobalProducts, useGlobalBoxes } from "../../hooks/useProducts";
import {
  useCartManager,
  useWishlistManager,
  useProductNavigation,
} from "../../hooks/useProductActions";

import { getProductId, getProductName } from "../../utils";
import {
  trackOmnisendBoxesViewed,
  trackOmnisendCheckoutStarted,
} from "../../omnisend";
import { sanitizeProductName } from "../../utils";
import { fetchCompareProducts } from "../../services/productService";
import {
  trackCrossSellViewed,
  trackCrossSellAdded,
} from "../../services/cartSuggestionTracking";
import {
  getPriceChangeMessage,
  reconcileCartPrices,
} from "../../utils/cartPricing";
import { useCartPriceSync } from "../../hooks/useCartPriceSync";

const CartOffCanvas = () => {
  const navigate = useNavigate();
  const {
    showCart,
    showToast,
    updateCart,
    cartItems,
    setCartItems,
    isMobile,
    handleCloseCart,
    calculateTotalAmount,
  } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showAuthButtons, setShowAuthButtons] = useState(false);
  const [navHeight, setNavHeight] = useState(0);

  const userData = getParsedLocalStorageItem("userData");
  const token = localStorageGetItem("userToken");
  const isLoggedIn = !!userData && !!token;
  const totalAmount = calculateTotalAmount();
  const totalItems = cartItems.reduce(
    (total, item) => total + item?.quantity,
    0,
  );

  const cartProductIds = cartItems.map((item) => item?.id || item?._id);
  const cartProductIdsKey = cartProductIds.join(",");

  const {
    data: globalProducts = [],
    isLoading: productsLoading,
    dataUpdatedAt: productsUpdatedAt,
  } = useGlobalProducts();
  const {
    data: subscriptionBoxes = [],
    isLoading: boxesLoading,
    dataUpdatedAt: boxesUpdatedAt,
  } = useGlobalBoxes();

  // Re-prices the cart from the server whenever it is on screen. Silent — the
  // only place a price change is announced is the Checkout click below.
  const { priceOverrideFor, refreshProductCaches } = useCartPriceSync({
    enabled: isMobile || showCart,
  });

  const { getCartSuggestedProducts } = productRoute();
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [suggestionMeta, setSuggestionMeta] = useState({
    suggestionSetId: null,
    cartSignature: null,
  });

  const { handleIncreaseQuantity, handleDecreaseQuantity } = useCartManager(
    [...globalProducts, ...cartItems],
    subscriptionBoxes,
    "Cart Page",
  );

  const allDisplayItems = useMemo(() => {
    return [...cartItems, ...suggestedProducts];
  }, [cartItems, suggestedProducts]);

  const {
    toggleFill: hookToggleFill,
    isInWishlist: hookIsInWishlist,
    isWishlistPending: hookIsWishlistPending,
  } = useWishlistManager(allDisplayItems);

  const { handleShowProductModal } = useProductNavigation();

  useEffect(() => {
    if (productsLoading || boxesLoading) return;

    setCartItems((prevItems) => {
      const updatedItems = prevItems.map((item) => {
        if (item?.productName) {
          const updatedProduct = globalProducts?.find(
            (product) =>
              (product?.id || product?._id) === (item?.id || item?._id),
          );
          return updatedProduct
            ? {
                ...item,
                price: updatedProduct.price,
                stockLevel: updatedProduct.stockLevel,
                quantityAvailable: updatedProduct.quantityAvailable,
                shoppingInstructions: updatedProduct.shoppingInstructions,
                productImage: updatedProduct.productImage,
                size: updatedProduct.size,
                productName: updatedProduct.productName,
                discountPrice: updatedProduct?.discountPrice || "",
                discountName: updatedProduct?.discountName || "",
                // These lists come from a 24h persisted cache, so they can
                // carry prices older than what the server already confirmed.
                ...priceOverrideFor(item, productsUpdatedAt),
              }
            : item;
        } else if (item?.name) {
          // Update subscription box items by matching with subscriptionBoxes
          const updatedBox = subscriptionBoxes?.find(
            (box) => (box?.id || box?._id) === (item?.id || item?._id),
          );
          return updatedBox
            ? {
                ...item,
                price: updatedBox.amount,
                description: updatedBox.description,
                boxImage: updatedBox?.boxImage,
                name: updatedBox.name,
                status: updatedBox?.status || "",
                ...priceOverrideFor(item, boxesUpdatedAt),
              }
            : item;
        }
        return item;
      });

      localStorageSetItem("cartItems", JSON.stringify(updatedItems));
      return updatedItems;
    });
  }, [globalProducts, subscriptionBoxes, boxesLoading, productsLoading]);

  useEffect(() => {
    if (cartProductIds.length > 0) {
      getCartSuggestedProducts(
        { products: cartProductIds },
        setLoading,
        showToast,
        (data) => {
          setSuggestedProducts(
            Array.isArray(data?.products) ? data.products : [],
          );
          setSuggestionMeta({
            suggestionSetId: data?.suggestionSetId || null,
            cartSignature: data?.cartSignature || null,
          });
        },
      );
    } else {
      setSuggestedProducts([]);
      setSuggestionMeta({ suggestionSetId: null, cartSignature: null });
    }
  }, [cartProductIdsKey]);

  useEffect(() => {
    if (!isMobile) return;
    const measure = () => {
      const nav = document.querySelector(".fixed-bottom");
      setNavHeight(nav ? nav.offsetHeight : 0);
    };
    measure();
    const t = setTimeout(measure, 300);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
    };
  }, [isMobile]);

  const handleShowBoxModal = (subscription) => {
    setSelectedSubscription(subscription?.id);
    setShowSubscriptionModal(true);
    trackEvent(`Food Bundle Viewed`, {
      Box: subscription?.name,
      Location: "cart",
      User: userData?.email || "Anonymous",
    });
    if (userData?.email) {
      trackOmnisendBoxesViewed(userData?.email, {
        id: subscription?.id,
        title: subscription?.name,
        price: subscription?.amount,
        image: subscription?.boxImage,
        url: `${window.location.origin}/foodbundles`,
      });
    }
  };

  const handleCloseSubscriptionModal = () => {
    setShowSubscriptionModal(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleRemoveItem = (id) => {
    setCartItems((prevCartTags) => {
      const newCart = prevCartTags.filter((i) => getProductId(i) !== id);

      if (typeof window !== "undefined") {
        try {
          localStorageSetItem("cartItems", JSON.stringify(newCart));
          updateCart(newCart);
        } catch (e) {}
      }
      return newCart;
    });

    const item = cartItems.find((i) => getProductId(i) === id);
    if (item) {
      const name = getProductName(item);
      trackEvent(`Removed from Cart`, {
        "Product ID": id,
        "Product Name": name,
        "Category Name": item?.category || "",
        "Quantity Added": item?.quantity,
        User: userData?.email || "Anonymous",
      });
    }
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      return;
    }
    const productsInCart = cartItems?.filter((item) => item?.productName);
    const subscriptionBoxesInCart = cartItems?.filter((item) => item?.name);

    setIsValidating(true);
    let currentGlobalProducts = [];
    let currentSubscriptionBoxes = [];

    if (productsInCart.length > 0 || subscriptionBoxesInCart.length > 0) {
      try {
        const productIds = productsInCart.map((item) => getProductId(item));
        const boxIds = subscriptionBoxesInCart.map((item) =>
          getProductId(item),
        );

        const compareResult = await fetchCompareProducts({
          productIds,
          boxIds,
        });
        currentGlobalProducts =
          compareResult?.products ||
          (Array.isArray(compareResult) ? compareResult : []);
        currentSubscriptionBoxes =
          compareResult?.boxes || (Array.isArray(compareResult) ? [] : []);
      } catch (err) {
        console.error("Comparison fetch failed", err);
        setIsValidating(false);
        showToast({
          toastText:
            "Unable to validate items. Please check your connection and try again.",
          isSuccess: false,
        });
        return;
      }
    }

    const invalidSubscriptions = subscriptionBoxesInCart?.filter((item) => {
      const box = currentSubscriptionBoxes?.find(
        (b) => getProductId(b) === getProductId(item),
      );
      return !box || box?.status === "inactive";
    });

    if (invalidSubscriptions?.length > 0) {
      setIsValidating(false);
      const boxNames = invalidSubscriptions
        .map((item) => item?.name || "Unknown")
        .join(", ");
      showToast({
        toastText: `The following subscription box(es) are unavailable: ${boxNames}.`,
        isSuccess: false,
      });
      return;
    }

    const invalidItems = productsInCart?.filter((item) => {
      const product = currentGlobalProducts?.find(
        (p) => getProductId(p) === getProductId(item),
      );
      return (
        !product ||
        product?.stockLevel === "out_of_stock" ||
        product?.quantityAvailable === 0 ||
        item?.quantity > product?.quantityAvailable
      );
    });

    if (invalidItems?.length > 0) {
      setIsValidating(false);
      let outOfStockMessage = "";
      let lowQuantityMessage = "";

      invalidItems?.forEach((item) => {
        const product = currentGlobalProducts?.find(
          (p) => getProductId(p) === getProductId(item),
        );
        if (
          !product ||
          product?.stockLevel === "out_of_stock" ||
          product?.quantityAvailable === 0
        ) {
          outOfStockMessage += `${item.productName}, `;
        } else {
          lowQuantityMessage += `We only have ${product?.quantityAvailable} items in stock for ${item.productName}. `;
        }
      });

      let toastText = "";
      if (outOfStockMessage) {
        toastText += `${outOfStockMessage.trim()} ${
          outOfStockMessage.length > 1 ? "are" : "is"
        } out of stock. `;
      }
      if (lowQuantityMessage) {
        toastText += `${lowQuantityMessage.trim()} `;
      }

      showToast({ toastText: toastText.trim(), isSuccess: false });
      return;
    }

    // Last gate: the fetch above is authoritative, so anything that moved since
    // the user last looked gets confirmed before the order is built.
    const { repricedCart, priceChanges } = reconcileCartPrices(
      cartItems,
      currentGlobalProducts,
      currentSubscriptionBoxes,
    );

    if (priceChanges.length > 0) {
      setIsValidating(false);
      updateCart(repricedCart);
      refreshProductCaches();
      showToast({
        toastText: "Price update",
        subText: getPriceChangeMessage(priceChanges),
        // "warning" is the blue variant in showToast; "info" is amber.
        type: "warning",
      });
      return;
    }

    const minimumAmountRequired = 20000;

    if (totalAmount >= minimumAmountRequired) {
      setIsValidating(false);
      const cartForTracking = {
        id: "main_cart",
        products: cartItems.map((item) => ({
          id: getProductId(item),
          title: getProductName(item),
          price: item.price,
          quantity: item.quantity,
          url: item?.boxImage
            ? `${window.location.origin}/foodbundles`
            : `${window.location.origin}/products/${sanitizeProductName(
                getProductName(item),
              )}/${item.sku}`,
          image: item.productImage || item.boxImage,
        })),
        total: totalAmount,
      };
      trackOmnisendCheckoutStarted(userData?.email, cartForTracking);
      trackEvent("Checkout Click", {
        total_amount: totalAmount,
        number_of_items: totalItems,
        products: cartItems.map((item) => ({
          id: getProductId(item),
          title: getProductName(item),
          price: item.price,
          quantity: item.quantity,
          image: item.productImage || item.boxImage,
        })),
        user: userData?.email || "Anonymous",
      });
    }

    if (totalAmount < minimumAmountRequired) {
      setIsValidating(false);
    } else {
      setIsValidating(false);
      if (isLoggedIn) {
        handleCloseCart();
        navigate("/order-summary");
      } else {
        setShowAuthButtons(true);
      }
    }
  };

  const isCheckoutLoading = isValidating;
  const MINIMUM_BASKET = 20000;
  const isCheckoutDisabled = totalAmount < MINIMUM_BASKET;
  // Derived in render so it updates instantly with the total (no effect lag)
  const additionalAmountNeeded = Math.max(0, MINIMUM_BASKET - totalAmount);

  const availableSuggestions = suggestedProducts.filter(
    (p) => !cartItems.some((item) => getProductId(item) === getProductId(p)),
  );

  const suggestionContext = {
    suggestionSetId: suggestionMeta.suggestionSetId,
    cartSignature: suggestionMeta.cartSignature,
    cartProductIds,
    suggestedProductIds: suggestedProducts.map((p) => getProductId(p)),
  };

  const getSuggestionRank = (id) => {
    const idx = suggestedProducts.findIndex((p) => getProductId(p) === id);
    const product = idx >= 0 ? suggestedProducts[idx] : null;
    if (typeof product?.rank === "number") return product.rank;
    return idx >= 0 ? idx : 0;
  };

  const handleSuggestionAdd = (id) => {
    handleIncreaseQuantity(id);
    trackCrossSellAdded(suggestionContext, {
      suggestedProductId: id,
      rank: getSuggestionRank(id),
      quantity: 1,
    });
  };

  const handleSuggestionShowModal = (product) => {
    trackCrossSellViewed(suggestionContext, {
      suggestedProductId: getProductId(product),
      rank: getSuggestionRank(getProductId(product)),
    });
    handleCloseCart();
    handleShowProductModal(product);
  };

  const renderCartContent = (wrapperClass = "") => (
    <div className={wrapperClass}>
      {cartItems?.length > 0 ? (
        <div className="mb-3 pb-3">
          <div className="mb-2 px-3">
            <CartIncentive totalAmount={totalAmount} isMobile={isMobile} />
          </div>
          <div className="px-3">
            <p
              className="d-block d-md-none"
              style={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#7c8593",
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                margin: "0 2px 6px",
              }}
            >
              {totalItems} {totalItems === 1 ? "item" : "items"}
            </p>
            {cartItems.map((item) => (
              <CartItem
                key={getProductId(item)}
                item={item}
                isMobile={isMobile}
                onIncrease={(id) => handleIncreaseQuantity(id)}
                onDecrease={handleDecreaseQuantity}
                onRemove={handleRemoveItem}
                onToggleWishlist={hookToggleFill}
                onShowBoxModal={handleShowBoxModal}
                onOpenProduct={(product) => {
                  handleCloseCart();
                  handleShowProductModal(product);
                }}
                isInWishlist={hookIsInWishlist(getProductId(item))}
                isWishlistPending={hookIsWishlistPending(getProductId(item))}
              />
            ))}
          </div>

          {/* Summary Section */}
          <div className="mx-3">
            <div
              style={
                isMobile
                  ? {
                      background: "#ffffff",
                      border: "1px solid #eceef0",
                      borderRadius: "16px",
                      padding: "15px",
                    }
                  : { padding: "18px 0 6px" }
              }
            >
              <h3
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#7c8593",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "12px",
                }}
              >
                Summary
              </h3>
              <div className="d-flex justify-content-between align-items-center">
                <span
                  style={{
                    color: "#41474f",
                    fontWeight: 600,
                    fontSize: "13.5px",
                  }}
                >
                  Subtotal ({totalItems} items)
                </span>
                <span
                  style={{
                    color: "#23272f",
                    fontWeight: 700,
                    fontSize: "14px",
                  }}
                >
                  {new Intl.NumberFormat("en-NG", {
                    style: "currency",
                    currency: "NGN",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(totalAmount)}
                </span>
              </div>
              <p
                style={{
                  fontSize: "11.5px",
                  color: "#7c8593",
                  fontWeight: 600,
                  marginTop: "8px",
                  marginBottom: 0,
                }}
              >
                Delivery fees & order total are calculated in the the next step
              </p>
            </div>
          </div>

          {/* Min Amount Warning */}
          {additionalAmountNeeded > 0 && (
            <div
              style={{
                border: "1px solid #557c2e",
                borderLeft: "4px solid #557c2e",
                backgroundColor: "#eef4e7",
                borderRadius: "12px",
              }}
              className="p-3 mt-3 mx-3"
            >
              <p
                className="my-auto"
                style={{
                  color: "#33502a",
                  fontSize: "12.5px",
                  fontWeight: 600,
                }}
              >
                Add items worth at least{" "}
                <span style={{ fontWeight: "800" }}>
                  {new Intl.NumberFormat("en-NG", {
                    style: "currency",
                    currency: "NGN",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(additionalAmountNeeded)}
                </span>{" "}
                to reach the <span style={{ fontWeight: "800" }}>₦20,000</span>{" "}
                minimum basket amount.
              </p>
            </div>
          )}
        </div>
      ) : (
        <CartEmptyState
          onContinueShopping={() => {
            navigate("/");
            handleCloseCart();
          }}
        />
      )}

      {cartItems.length > 0 && (
        <SuggestedProducts
          isInWishlist={hookIsInWishlist}
          isWishPending={hookIsWishlistPending}
          onToggleFill={hookToggleFill}
          variant={isMobile ? "full" : "compact"}
          products={availableSuggestions}
          isMobile={isMobile}
          title="You may also like"
          className="px-2 px-md-0"
          style={{ paddingBottom: "32px" }}
          onAdd={handleSuggestionAdd}
          onShowModal={handleSuggestionShowModal}
        />
      )}
    </div>
  );

  const secondaryBtnStyle = {
    backgroundColor: "#ffffff",
    color: "#23272f",
    fontWeight: 800,
    fontSize: "15px",
    borderRadius: "12px",
    border: "1.5px solid #d9dde1",
    padding: "14px",
  };

  const primaryBtnStyle = (disabled) => ({
    backgroundColor: disabled ? "#c2cbb4" : "#557c2e",
    color: "#fff",
    fontWeight: 800,
    fontSize: "15px",
    borderRadius: "12px",
    border: "none",
    padding: "14px",
  });

  const renderFooter = ({ mobile } = {}) => {
    if (cartItems.length === 0) return null;

    if (mobile) {
      return (
        <>
          <div className="d-flex justify-content-between align-items-baseline mb-1">
            <span
              style={{ fontSize: "12px", color: "#7c8593", fontWeight: 700 }}
            >
              Subtotal · {totalItems} {totalItems === 1 ? "item" : "items"}
            </span>
            <span
              style={{ fontSize: "16px", fontWeight: 800, color: "#23272f" }}
            >
              {new Intl.NumberFormat("en-NG", {
                style: "currency",
                currency: "NGN",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(totalAmount)}
            </span>
          </div>
          <Button
            variant="cart-btn"
            className="w-100 d-flex justify-content-center align-items-center gap-2"
            style={{
              ...primaryBtnStyle(isCheckoutDisabled),
              padding: "9px",
              height: "42px",
              fontSize: "14px",
            }}
            disabled={isCheckoutDisabled || isCheckoutLoading}
            onClick={handleCheckout}
          >
            {isCheckoutLoading ? (
              <span
                className="spinner-border spinner-border-sm text-white"
                role="status"
              />
            ) : (
              <>
                Checkout
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.4"
                >
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </>
            )}
          </Button>
        </>
      );
    }

    if (showAuthButtons && !isLoggedIn) {
      return (
        <div className="d-flex gap-3">
          <Button
            variant="cart-btn"
            className="w-100"
            style={secondaryBtnStyle}
            onClick={() => {
              handleCloseCart();
              navigate("/login");
              trackEvent("Sign in Click", {
                clicked_at: new Date().toISOString(),
                location_clicked: transformPath(window.location.pathname),
                button_clicked: "Sign in",
              });
            }}
          >
            Sign in
          </Button>
          <Button
            variant="cart-btn"
            className="w-100"
            style={primaryBtnStyle(false)}
            onClick={() => {
              handleCloseCart();
              navigate("/signup");
              trackEvent("Create an Account Click", {
                clicked_at: new Date().toISOString(),
                location_clicked: transformPath(window.location.pathname),
                button_clicked: "Create an account",
              });
            }}
          >
            Create Account
          </Button>
        </div>
      );
    }

    return (
      <div className="d-flex gap-3">
        <Button
          variant="cart-btn"
          className="w-100"
          style={secondaryBtnStyle}
          onClick={() => {
            navigate("/");
            handleCloseCart();
          }}
        >
          Continue Shopping
        </Button>
        <Button
          variant="cart-btn"
          className="w-100 d-flex justify-content-center align-items-center"
          style={{ ...primaryBtnStyle(isCheckoutDisabled), height: "52px" }}
          disabled={isCheckoutDisabled || isCheckoutLoading}
          onClick={handleCheckout}
        >
          {isCheckoutLoading ? (
            <span
              className="spinner-border spinner-border-sm text-white"
              role="status"
            />
          ) : (
            "Checkout"
          )}
        </Button>
      </div>
    );
  };
  const renderAuthSheet = () => {
    const open = showAuthButtons && !isLoggedIn;
    const closeSheet = () => setShowAuthButtons(false);

    return (
      <>
        <div
          onClick={closeSheet}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(20,24,18,.45)",
            opacity: open ? 1 : 0,
            pointerEvents: open ? "auto" : "none",
            transition: "opacity .25s ease",
            zIndex: 10000,
          }}
        />
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            background: "#fff",
            borderRadius: "26px 26px 0 0",
            padding: "10px 22px calc(22px + env(safe-area-inset-bottom))",
            transform: open ? "translateY(0)" : "translateY(110%)",
            transition: "transform .3s cubic-bezier(.22,1,.36,1)",
            zIndex: 10001,
            boxShadow: "0 -16px 40px rgba(0,0,0,.18)",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "4px",
              borderRadius: "99px",
              background: "#dfe2e6",
              margin: "0 auto 16px",
            }}
          />
          <h2
            style={{
              fontSize: "20px",
              fontWeight: 800,
              marginBottom: "6px",
              color: "#23272f",
            }}
          >
            Sign in to check out
          </h2>
          <p
            style={{
              fontSize: "13.5px",
              color: "#7c8593",
              fontWeight: 600,
              lineHeight: 1.4,
              marginBottom: "20px",
            }}
          >
            Your cart is saved. Subtotal{" "}
            <b style={{ color: "#23272f" }}>
              {new Intl.NumberFormat("en-NG", {
                style: "currency",
                currency: "NGN",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(totalAmount)}
            </b>{" "}
            · {totalItems} {totalItems === 1 ? "item" : "items"}.
          </p>

          <Button
            variant="cart-btn"
            className="w-100 mb-2"
            style={{ ...primaryBtnStyle(false), padding: "16px" }}
            onClick={() => {
              closeSheet();
              handleCloseCart();
              localStorageSetItem("preAuthPath", "/cart");
              navigate("/signup");
              trackEvent("Create an Account Click", {
                clicked_at: new Date().toISOString(),
                location_clicked: transformPath(window.location.pathname),
                button_clicked: "Create an account",
              });
            }}
          >
            Create account
          </Button>
          <Button
            variant="cart-btn"
            className="w-100"
            style={{ ...secondaryBtnStyle, padding: "15px" }}
            onClick={() => {
              closeSheet();
              handleCloseCart();
              localStorageSetItem("preAuthPath", "/cart");
              navigate("/login");
              trackEvent("Sign in Click", {
                clicked_at: new Date().toISOString(),
                location_clicked: transformPath(window.location.pathname),
                button_clicked: "Sign in",
              });
            }}
          >
            Sign in
          </Button>
          <p
            style={{
              textAlign: "center",
              fontSize: "11.5px",
              color: "#7c8593",
              fontWeight: 600,
              margin: "16px 0 0",
            }}
          >
            Delivery options &amp; payment come next
          </p>
        </div>
      </>
    );
  };

  return (
    <>
      {isMobile ? (
        <MobilePageShell
          title="Cart"
          titleVariant="plain"
          onBack={handleBack}
          bottomGap={0}
          className="px-lg-5 mx-lg-5"
          style={{ backgroundColor: "#f6f7f8", minHeight: "100vh" }}
        >

          {renderCartContent()}

          {cartItems.length > 0 && (
            // key on the sticky element itself forces React to replace the
            // whole node (and its cached compositor layer) when the total
            // changes — otherwise mobile browsers keep showing a stale,
            // unrepainted subtotal on the "stuck" bar.
            <div
              key={`footer-${totalItems}-${totalAmount}`}
              style={{
                position: "sticky",
                bottom: `${navHeight}px`,
                zIndex: 1000,
                background: "#fff",
                borderTop: "1px solid #eceef0",
                boxShadow: "0 -8px 22px rgba(0,0,0,.05)",
                padding: "7px 16px 8px",
              }}
            >
              {renderFooter({ mobile: true })}
            </div>
          )}

          {!isLoggedIn && renderAuthSheet()}
        </MobilePageShell>
      ) : (
        <Offcanvas
          size="lg"
          show={showCart}
          placement="end"
          style={{
            backgroundColor: "#ffffff",
            display: "flex",
            flexDirection: "column",
          }}
          onHide={handleCloseCart}
        >
          <div className="d-flex gap-3 align-items-center px-5 px-md-3 pt-4 pb-2">
            <img src={arrowLeft} onClick={handleCloseCart} alt="arrow" />
            <h5
              className="my-auto"
              style={{ color: "#23272f", fontWeight: 800, fontSize: "21px" }}
            >
              Cart
            </h5>
          </div>
          <Offcanvas.Body style={{ flex: 1, overflowY: "auto" }}>
            {renderCartContent("px-1 px-md-3")}
          </Offcanvas.Body>
          {cartItems.length > 0 && (
            <div
              style={{
                flex: "none",
                borderTop: "1px solid #eceef0",
                background: "#fff",
                padding: "18px 24px 30px",
                boxShadow: "0 -8px 22px rgba(0,0,0,.05)",
              }}
            >
              {renderFooter()}
            </div>
          )}
        </Offcanvas>
      )}

      <SubscriptionModal
        showModal={showSubscriptionModal}
        handleCloseModal={handleCloseSubscriptionModal}
        boxId={selectedSubscription}
      />
    </>
  );
};

export default CartOffCanvas;
